import { AlertColor } from "@mui/material";
import Modal from "../../../components/common/Modal";
import {
  CashOutAmount,
  ESenderRole,
  ETrasactionsType,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import CustomizedSnackbars from "../../../components/customizedSnackbars";
import {
  ADDRESS,
  BANK_ACCOUNT_NOT_ADDED,
  BANK_ACCOUNT_NUMBER,
  BANK_TRANSFER_HEADER,
  BENEFICIARY_NAME,
  CHOOSE_REFUND_METHOD,
  CHOOSE_TRANSFER_METHOD,
  COMPLETE_CONVERSION,
  CONVERSION_IN_PROGRESS,
  MANUAL,
  MANUAL_BANK_TRANSFER,
  MOYASAR,
  MOYASAR_PLATFORM,
  MOYASAR_TRANSER_HEADER,
  OTHER_REFUND_METHODS,
  RIYAL,
  SElECTION,
  SINGLE_VIEW,
  SINGLE_VIEW_PLATFORM,
  SINGLE_VIEW_TRANSER_HEADER,
  SUCCESS_MESSAGE,
  TABBY,
  TABBY_PLATFORM,
  TABBY_TRANSER_HEADER,
  THE_AMOUNT_TO_BE_TRANSFERED,
  THE_BANK,
  TOTAL_DUE,
  TRANSACTION_DATE,
  TRANSFER_TO_REFERENCE_NUMBER,
  UNAVAILABLE,
  WAITING_FOR_CONVERSION,
} from "./constants";

import styles from "./styles.module.scss";
import Spinner from "../../../components/common/Spinner";
import classNames from "classnames";
import { ContractContext } from "../../../providers/contract";
import {
  checkCashoutPayment,
  checkUserBank,
  makeCashOutPayment,
  makeManualCashOutPayment,
  refundTransaction,
} from "../../../setup/api/services/payment";
import { getUserDetailsById } from "../../../setup/api/services/user";
import {
  BankResponseData,
  ICashoutRequest,
} from "../../../setup/api/services/payment/models";
import {
  IUserAddressResponse,
  IUserInfoResponseData,
} from "../../../setup/api/services/user/models";
import { COUNTRY } from "../../userStats/constant";
import TextInput, { InputType } from "../../../components/forms/textInput";
import PrimaryButton from "../../../components/common/PrimaryButton";
import DateTimePicker from "../../../components/common/DateTimePicker";
import {
  ACTION_UNDER_PROCESSING,
  CANNOT_MAKE_THIS_CALL,
} from "../../contract/constants";

interface INewCashoutModal {
  userType: ESenderRole;
  cashOutAmount: CashOutAmount;
  open: boolean;
  onClose: () => void;
  user: IUserInfoResponseData | Partial<UserProfile> | undefined;
}

const NewCashoutModal = ({
  userType,
  cashOutAmount,
  open,
  onClose,
  user,
}: INewCashoutModal) => {
  const { contract } = useContext(ContractContext);

  const [selectionType, setSelectionType] = useState<SElECTION>(SElECTION.NONE);

  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>();
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  const [showPending, setShowPending] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const [amount, setAmount] = useState<number | string>();

  const [transactionDate, setTransactionDate] = useState<Date | null>(
    new Date()
  );

  const [bankDetails, setBankDetails] = useState<
    BankResponseData | undefined
  >();
  const [address, setUserAddress] = useState<
    IUserAddressResponse["data"] | undefined
  >(undefined);

  const [isPending, setIsPending] = useState<boolean>(false);

  const isDisabled = useMemo(() => {
    if (selectionType === SElECTION.BANK) {
      if (bankDetails && amount) return false;
      else return true;
    }
    if (
      selectionType === SElECTION.SINGLE_VIEW ||
      selectionType === SElECTION.TABBY ||
      selectionType === SElECTION.MOYASAR
    ) {
      if (
        ((!bankDetails || !address) && userType !== ESenderRole.CUSTOMER) ||
        !amount
      ) {
        return true;
      }
      if (Number(amount) > cashOutAmount.cashOutAmount) return true;
    }
    return false;
  }, [address, amount, bankDetails, userType, selectionType, cashOutAmount]);

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  const setSnackBarStates = useCallback((title: string, color: AlertColor) => {
    setSnackBarOpen(true);
    setSnackBarColor(color);
    setSnackBarTitle(title);
  }, []);

  const handleSelection = useCallback(
    async (selection: SElECTION) => {
      setIsPending(true);
      try {
        const bankDetails = await checkUserBank(user?.userIdentification);
        setBankDetails(bankDetails?.data);
      } catch (error) {
        console.error(error);
      }
      try {
        const result = await getUserDetailsById(
          Number(user?.userIdentification)
        );
        setUserAddress(result?.data?.profile?.addresses[0]);
        setSelectionType(selection);
      } catch (error) {
        setSelectionType(selection);
        console.error(error);
      }
      setIsPending(false);
    },
    [user]
  );

  const checkCashoutStatus = useCallback(async () => {
    try {
      await checkCashoutPayment(
        contract?.transactions
          ? contract?.transactions[0]?.referenceTransactionId
          : "",
        String(user?.userIdentification)
      );
      return;
    } catch (err) {
      console.error(err);
    }
  }, [contract, user]);

  const handleClose = useCallback(() => {
    setIsPending(false);
    setBankDetails(undefined);
    setUserAddress(undefined);
    setShowPending(false);
    setTransactionDate(new Date());
    onClose();

    setSelectionType(SElECTION.NONE);
  }, [onClose]);

  const onConfirm = useCallback(async () => {
    if (contract && user) {
      setShowPending(true);
      // this body is used for cashout and refund
      const cashoutBody: ICashoutRequest = {
        amount: 0,
        referenceTransactionId: "",
      };

      if (amount && contract?.transactions) {
        cashoutBody.referenceTransactionId = contract?.transactions
          ? contract?.transactions[0]?.referenceTransactionId
          : "";
        cashoutBody.amount = +amount || 0;
      }

      try {
        let cashoutRes;
        let refundRes;
        let normalRes;
        if (amount && contract?.transactions) {
          if (
            userType === ESenderRole.CUSTOMER &&
            (selectionType === SElECTION.TABBY ||
              selectionType === SElECTION.MOYASAR)
          ) {
            cashoutBody.domainId = contract.shortId;
            refundRes = await refundTransaction(cashoutBody);
          } else if (selectionType === SElECTION.SINGLE_VIEW) {
            cashoutBody.assignedBeneficiaryId = String(user.userIdentification);
            cashoutBody.assignedBeneficiaryName = user.profile?.fullName || "";
            cashoutRes = await makeCashOutPayment(cashoutBody);
          }
        }

        if (cashoutRes) {
          setSnackBarStates(
            cashoutRes?.data?.statusDescription || ACTION_UNDER_PROCESSING,
            cashoutRes?.data?.status !== "FAILED" ? "success" : "error"
          );
          if (cashoutRes?.data?.status !== "FAILED") checkCashoutStatus();
        } else if (refundRes) {
          setSnackBarStates(
            ACTION_UNDER_PROCESSING,
            refundRes?.data?.status !== "FAILED" ? "success" : "error"
          );
        } else {
          if (selectionType === SElECTION.BANK) {
            cashoutBody.assignedBeneficiaryId = String(user.userIdentification);
            cashoutBody.assignedBeneficiaryName = user.profile?.fullName || "";
            cashoutBody.amount = cashOutAmount.cashOutAmount;
            cashoutBody.gatewayPaymentId = String(amount);
            cashoutBody.transactionDate = transactionDate
              ? transactionDate.toISOString()
              : new Date().toISOString();
            normalRes = await makeManualCashOutPayment(cashoutBody);
            setSnackBarStates(
              ACTION_UNDER_PROCESSING,
              normalRes?.data?.status !== "FAILED" ? "success" : "error"
            );
          }
        }
        setShowSuccess(true);
        setShowPending(false);
      } catch (error) {
        setSnackBarStates(CANNOT_MAKE_THIS_CALL, "error");
        setShowSuccess(false);
        setShowPending(false);
        handleClose();
      }
    }
  }, [
    contract,
    user,
    amount,
    userType,
    selectionType,
    checkCashoutStatus,
    setSnackBarStates,
    handleClose,
    cashOutAmount,
    transactionDate,
  ]);

  const renderSelection = useCallback(() => {
    return (
      <div className={styles["container"]}>
        {isPending ? (
          <Spinner />
        ) : (
          <div
            style={{ width: "100%" }}
            className={classNames({
              [styles["horizontal_section_container"]]:
                userType !== ESenderRole.CUSTOMER,
            })}>
            {userType === ESenderRole.CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.paymentMethod === "TABBY" &&
                  (!transaction.fromUserId ||
                    transaction.fromUserId === String(user?.userIdentification))
              ) && (
                <div
                  onClick={() => handleSelection(SElECTION.TABBY)}
                  className={styles["wide_section"]}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/tabby.svg"
                  />
                  <div>
                    <h4>{TABBY}</h4>
                    <p className={styles["p2"]}>{TABBY_PLATFORM}</p>
                  </div>
                </div>
              )}
            {userType === ESenderRole.CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  (transaction?.paymentMethod === "CARD" ||
                    transaction?.paymentMethod === "APPLE_PAY") &&
                  (!transaction.fromUserId ||
                    transaction.fromUserId === String(user?.userIdentification))
              ) && (
                <div
                  onClick={() => handleSelection(SElECTION.MOYASAR)}
                  className={styles["wide_section"]}>
                  <img
                    style={{ height: "48px" }}
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/moyasar.svg"
                  />
                  <div>
                    <h4>{MOYASAR}</h4>
                    <p className={styles["p2"]}>{MOYASAR_PLATFORM}</p>
                  </div>
                </div>
              )}
            {userType === ESenderRole.CUSTOMER && (
              <h3 style={{ marginTop: "32px", marginBottom: "16px" }}>
                {OTHER_REFUND_METHODS}
              </h3>
            )}
            <div className={styles["payment_options"]}>
              <div
                onClick={() => handleSelection(SElECTION.BANK)}
                className={styles["section"]}>
                <img
                  className={styles["avatar_img"]}
                  src="/assets/images/svg/bank_transfer.svg"
                />
                <h4>{MANUAL}</h4>
                <p className={styles["p2"]}>{MANUAL_BANK_TRANSFER}</p>
              </div>
              {
                <div
                  onClick={() => handleSelection(SElECTION.SINGLE_VIEW)}
                  className={styles["section"]}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/single_view.svg"
                  />
                  <h4>{SINGLE_VIEW}</h4>
                  <p className={styles["p2"]}>{SINGLE_VIEW_PLATFORM}</p>
                </div>
              }
            </div>
          </div>
        )}
      </div>
    );
  }, [userType, isPending, handleSelection, contract?.transactions, user]);

  const renderAccountDetails = useCallback(() => {
    return (
      <>
        <div className={styles["account_container"]}>
          {!bankDetails && userType !== ESenderRole.CUSTOMER && (
            <div className={styles["info_container"]}>
              <div className={styles["img_container"]}>
                <img src={"/assets/images/svg/exclamation.svg"} />
              </div>
              <p>{BANK_ACCOUNT_NOT_ADDED}</p>
            </div>
          )}
          <div className={styles["warning_message"]}></div>
          <div className={styles["details"]}>
            <p>{TOTAL_DUE}</p>
            <p className={styles["detail_amount"]}>
              {cashOutAmount.cashOutAmount?.toFixed(2)}
              <span>{RIYAL}</span>
            </p>
          </div>
          {selectionType !== SElECTION.TABBY &&
            selectionType !== SElECTION.MOYASAR && (
              <div className={styles["details"]}>
                <p>{BANK_ACCOUNT_NUMBER}</p>
                {bankDetails ? (
                  <p className={styles["detail_desc"]}>{bankDetails?.iban}</p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SElECTION.TABBY &&
            selectionType !== SElECTION.MOYASAR && (
              <div className={styles["details"]}>
                <p>{BENEFICIARY_NAME}</p>
                {bankDetails ? (
                  <p className={styles["detail_desc"]}>
                    {bankDetails?.beneficiaryName}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SElECTION.TABBY &&
            selectionType !== SElECTION.MOYASAR && (
              <div className={styles["details"]}>
                <p>{THE_BANK}</p>
                {bankDetails ? (
                  <p className={styles["detail_desc"]}>
                    {bankDetails?.bank?.arabicName}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SElECTION.TABBY &&
            selectionType !== SElECTION.MOYASAR && (
              <div className={styles["details"]}>
                <p>{ADDRESS}</p>
                {address ? (
                  <p className={styles["detail_desc"]}>
                    {COUNTRY[address?.countryCode]} - {address?.city} -
                    {address?.district} - {address?.street} -{" "}
                    {address?.buildingNo} -{address?.postalCode}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
        </div>
        <div className={styles["text_area"]}>
          <TextInput
            handleChange={(event) => setAmount(event.target.value)}
            type={
              selectionType !== SElECTION.BANK
                ? InputType.NUMBER
                : InputType.DEFAULT
            }
            defaultValue={amount}
            placeholder={
              selectionType !== SElECTION.BANK
                ? THE_AMOUNT_TO_BE_TRANSFERED
                : TRANSFER_TO_REFERENCE_NUMBER
            }
          />
        </div>
        {selectionType === SElECTION.BANK && (
          <div className={styles["text_area"]}>
            <DateTimePicker
              style={{ borderRadius: "0rem" }}
              value={transactionDate}
              placeholder={TRANSACTION_DATE}
              label={TRANSACTION_DATE}
              handleChange={(date) => {
                setTransactionDate(date);
              }}
            />
          </div>
        )}
        <div className={styles["button_container"]}>
          <PrimaryButton disabled={isDisabled} onClick={onConfirm}>
            {COMPLETE_CONVERSION}
          </PrimaryButton>
        </div>
      </>
    );
  }, [
    address,
    amount,
    bankDetails,
    userType,
    isDisabled,
    onConfirm,
    selectionType,
    cashOutAmount,
    transactionDate,
  ]);

  useEffect(() => {
    if (showSuccess) {
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
        window.location.reload();
      }, 5000);
    }
  }, [handleClose, showSuccess]);

  if (showPending) {
    return (
      <Modal size={"small"} visible={showPending} onCancel={handleClose}>
        <div className={styles["pending_container"]}>
          <img src="/assets/images/svg/pending.svg" />
          <h1>{CONVERSION_IN_PROGRESS}</h1>
          <p className={styles["p1"]}>{WAITING_FOR_CONVERSION}</p>
        </div>
      </Modal>
    );
  }

  if (showSuccess) {
    return (
      <Modal size={"small"} visible={showSuccess} onCancel={handleClose}>
        <div className={styles["success_container"]}>
          <img src="/assets/images/svg/Success_tick.svg" />
          <h1>{SUCCESS_MESSAGE}</h1>
        </div>
      </Modal>
    );
  }

  return (
    <Modal size={"medium"} visible={open} onCancel={handleClose}>
      {renderSnackBar()}
      {selectionType === SElECTION.NONE && (
        <h1>
          {userType === ESenderRole.CUSTOMER
            ? CHOOSE_REFUND_METHOD
            : CHOOSE_TRANSFER_METHOD}
        </h1>
      )}
      {selectionType === SElECTION.SINGLE_VIEW && (
        <h1>{SINGLE_VIEW_TRANSER_HEADER}</h1>
      )}
      {selectionType === SElECTION.TABBY && <h1>{TABBY_TRANSER_HEADER}</h1>}
      {selectionType === SElECTION.MOYASAR && <h1>{MOYASAR_TRANSER_HEADER}</h1>}
      {selectionType === SElECTION.BANK && <h1>{BANK_TRANSFER_HEADER}</h1>}
      {selectionType === SElECTION.NONE && <>{renderSelection()}</>}
      {selectionType !== SElECTION.NONE && <>{renderAccountDetails()}</>}
    </Modal>
  );
};

export default NewCashoutModal;
